/**
 * INKO — Daily Challenge (Wordle-style).
 *
 * One player per CET day (deterministic hash), single 30 s round,
 * global leaderboard, personal streak in localStorage, shareable result.
 *
 * Route: /inko/daily
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
    ArrowLeft, ArrowClockwise, Play, SkipForward, Trophy,
    Check, X, Clock, Star, Copy, Fire, User, Flag, Gauge, Eye, Globe,
} from "@phosphor-icons/react";
import AppShell from "../components/AppShell";
import playersData from "../data/inko-players.json";
import managersData from "../data/inko-managers.json";
import {
    normalize, isCorrect, isCorrectFuzzy, buildOptions,
    extractKeywords, autocompleteSearch, pacingConfig,
    microClueFor, MICRO_CLUE_LABELS, shuffleMicroClues, getPornoClues,
    surnameHintFor, SURNAME_LEAK_FIELDS,
    paidSurnameHint, paidSurnameHintCost, PAID_HINT_MIN_CLUE,
    computeRoundBonuses,
    getTier,
    getCrossFantasyProfile, getCrossFantasyLore, isManager,
    SCORE_TABLE, READING_MODES, READING_MODE_LABELS,
} from "../lib/inko";
import { ComboFilter } from "../components/inko/ComboFilter";
import { track } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---- Config -------------------------------------------------------------

const FEEDBACK_MS = 5000;
const LS_READING_MODE = "inko.reading_mode";

// ---- Utils --------------------------------------------------------------

/**
 * Return YYYY-MM-DD for the current day in Europe/Prague (CET/CEST).
 */
const cetToday = () => {
    const parts = new Intl.DateTimeFormat("en-CA", {
        timeZone: "Europe/Prague",
        year: "numeric", month: "2-digit", day: "2-digit",
    }).formatToParts(new Date());
    const y = parts.find((p) => p.type === "year")?.value;
    const m = parts.find((p) => p.type === "month")?.value;
    const d = parts.find((p) => p.type === "day")?.value;
    return `${y}-${m}-${d}`;
};

/** Deterministic hash → index. SHA-256 first 4 bytes → uint32. Same algo as backend. */
const seedFromDate = async (dateStr) => {
    const bytes = new TextEncoder().encode(dateStr);
    const hashBuf = await crypto.subtle.digest("SHA-256", bytes);
    const arr = new Uint8Array(hashBuf);
    return (arr[0] << 24 | arr[1] << 16 | arr[2] << 8 | arr[3]) >>> 0;
};

// ---- LocalStorage helpers ----------------------------------------------

const LS = {
    clientId: "inko.daily.client_id",
    displayName: "inko.daily.display_name",
    lastPlayed: "inko.daily.last_played",  // "YYYY-MM-DD"
    streak: "inko.daily.streak",
    bestScore: "inko.daily.best_score",
    playedModes: "inko.daily.played_modes", // "{date: {easy: score, hard: score}}"
};

const ensureClientId = () => {
    let id = localStorage.getItem(LS.clientId);
    if (!id) {
        id = "c-" + Math.random().toString(36).slice(2, 12) + Date.now().toString(36);
        localStorage.setItem(LS.clientId, id);
    }
    return id;
};

const getPlayedModes = () => {
    try {
        return JSON.parse(localStorage.getItem(LS.playedModes) || "{}");
    } catch {
        return {};
    }
};

const setPlayedForToday = (date, mode, score) => {
    const map = getPlayedModes();
    map[date] = { ...(map[date] || {}), [mode]: score };
    localStorage.setItem(LS.playedModes, JSON.stringify(map));
};

const updateStreak = (date, correct) => {
    const last = localStorage.getItem(LS.lastPlayed);
    let streak = parseInt(localStorage.getItem(LS.streak) || "0", 10) || 0;
    if (!correct) {
        streak = 0;
    } else if (last === date) {
        // already counted today
    } else {
        // Compute yesterday's YYYY-MM-DD purely from string (TZ-safe)
        const [y, m, d] = date.split("-").map(Number);
        const yDate = new Date(Date.UTC(y, m - 1, d));
        yDate.setUTCDate(yDate.getUTCDate() - 1);
        const yStr = yDate.toISOString().slice(0, 10);
        streak = last === yStr ? streak + 1 : 1;
    }
    localStorage.setItem(LS.streak, String(streak));
    localStorage.setItem(LS.lastPlayed, date);
    return streak;
};

const streakLabel = (n) => {
    if (n === 1) return "den";
    if (n >= 2 && n <= 4) return "dny";
    return "dní";
};

// ---- Main page ---------------------------------------------------------

export default function InkoDaily() {
    const [date] = useState(cetToday());
    const [dailyPlayer, setDailyPlayer] = useState(null);
    const [phase, setPhase] = useState("loading"); // loading | start | playing | result
    const [mode, setMode] = useState(null);        // easy | hard
    const [result, setResult] = useState(null);    // {score, clues_used, correct}
    const [displayName, setDisplayName] = useState(
        localStorage.getItem(LS.displayName) || ""
    );
    // Daily 1-player has a fixed profile: MIDDLE (classic) + MEDIUM for
    // a fair global leaderboard — no user picker.
    const [readingMode, setReadingMode] = useState("classic");
    const [streak, setStreak] = useState(
        parseInt(localStorage.getItem(LS.streak) || "0", 10) || 0
    );
    const [alreadyPlayed, setAlreadyPlayed] = useState(null); // {easy?, hard?}

    // Resolve daily player deterministically from CET date
    useEffect(() => {
        (async () => {
            const seed = await seedFromDate(date);
            const idx = seed % playersData.players.length;
            setDailyPlayer(playersData.players[idx]);
            setPhase("start");
        })();
    }, [date]);

    // Check localStorage for today's plays
    useEffect(() => {
        const map = getPlayedModes();
        setAlreadyPlayed(map[date] || {});
    }, [date]);

    const onStart = (selectedMode) => {
        if ((alreadyPlayed || {})[selectedMode] !== undefined) return;
        setMode(selectedMode);
        setPhase("playing");
    };

    const onFinish = useCallback(async (round) => {
        const nextStreak = updateStreak(date, round.correct);
        setStreak(nextStreak);
        setPlayedForToday(date, mode, round.score);
        setAlreadyPlayed((prev) => ({ ...(prev || {}), [mode]: round.score }));

        // Submit to backend
        const clientId = ensureClientId();
        const name = displayName.trim() || "Anonym";
        try {
            await axios.post(`${API}/inko/daily/submit`, {
                date,
                player_id: dailyPlayer.id,
                mode,
                reading_mode: readingMode,
                score: round.score,
                clues_used: round.cluesRevealed,
                correct: round.correct,
                display_name: name,
                client_id: clientId,
                time_taken_s: round.timeTaken,
            });
        } catch (e) {
            // Silent — local state still updates.
            console.warn("INKO daily submit failed:", e?.message);
        }
        try {
            track("inko_daily_play", {
                tool: "inko",
                meta: {
                    mode,
                    reading_mode: readingMode,
                    score: round.score,
                    correct: !!round.correct,
                    clues_used: round.cluesRevealed,
                    time_taken_s: round.timeTaken,
                },
            });
        } catch { /* silent */ }
        setResult({ ...round, mode, reading_mode: readingMode });
        setPhase("result");
    }, [date, dailyPlayer, displayName, mode, readingMode]);

    const bestBadge = useMemo(() => {
        const map = alreadyPlayed || {};
        return {
            easy: map.easy ?? null,
            medium: map.medium ?? null,
            hard: map.hard ?? null,
        };
    }, [alreadyPlayed]);

    return (
        <AppShell>
            <div className="max-w-4xl mx-auto px-3 sm:px-4 py-4 sm:py-8">
                <Header date={date} streak={streak} />

                {phase === "loading" && (
                    <div className="border border-white/10 bg-black/40 p-4 sm:p-8">
                        <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                            Načítám dnešního hráče…
                        </div>
                    </div>
                )}

                {phase === "start" && dailyPlayer && (
                    <StartScreen
                        date={date}
                        dailyPlayer={dailyPlayer}
                        streak={streak}
                        displayName={displayName}
                        setDisplayName={(n) => {
                            setDisplayName(n);
                            localStorage.setItem(LS.displayName, n);
                        }}
                        readingMode={readingMode}
                        setReadingMode={(m) => {
                            setReadingMode(m);
                            localStorage.setItem(LS_READING_MODE, m);
                        }}
                        alreadyPlayed={bestBadge}
                        onStart={onStart}
                    />
                )}

                {phase === "playing" && dailyPlayer && (
                    <Round
                        key={mode + date + readingMode}
                        player={dailyPlayer}
                        allPlayers={playersData.players}
                        mode={mode}
                        readingMode={readingMode}
                        onFinish={onFinish}
                    />
                )}

                {phase === "result" && dailyPlayer && result && (
                    <ResultScreen
                        date={date}
                        dailyPlayer={dailyPlayer}
                        result={result}
                        streak={streak}
                        displayName={displayName || "Anonym"}
                        onBack={() => setPhase("start")}
                    />
                )}
            </div>
        </AppShell>
    );
}

// ---- Header -----------------------------------------------------------

function Header({ date, streak }) {
    return (
        <div className="mb-6 flex items-center justify-between">
            <Link
                to="/inko"
                data-testid="inko-daily-back"
                className="font-mono text-[11px] uppercase tracking-widest text-white/50 hover:text-fa-cream flex items-center gap-2"
            >
                <ArrowLeft size={12} weight="bold" /> INKO
            </Link>
            <div className="flex items-center gap-4">
                {streak > 0 && (
                    <div
                        className="flex items-center gap-1.5 border border-fa-yellow/40 bg-fa-yellow/10 px-2 py-1"
                        data-testid="inko-daily-streak"
                    >
                        <Fire size={12} weight="fill" className="text-fa-yellow" />
                        <span className="font-mono text-[11px] uppercase tracking-widest text-fa-yellow">
                            {streak} {streakLabel(streak)}
                        </span>
                    </div>
                )}
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
                    INKO · Daily · {date}
                </div>
            </div>
        </div>
    );
}

// ---- Start screen -----------------------------------------------------

function StartScreen({ date, dailyPlayer, streak, displayName, setDisplayName, readingMode, setReadingMode, alreadyPlayed, onStart }) {
    const pacing = pacingConfig(readingMode);
    return (
        <div className="border border-white/10 bg-black/40 p-4 sm:p-8" data-testid="inko-daily-start">
            <div className="mb-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-yellow mb-2">
                    Denní výzva
                </div>
                <h1 className="font-mono text-4xl sm:text-5xl uppercase tracking-tight text-fa-cream">
                    Hráč dne
                </h1>
                <div className="mt-2 font-mono text-[11px] uppercase tracking-widest text-white/50">
                    1 hráč · 30 s · sdílitelný výsledek · globální žebříček
                </div>
            </div>

            {/* Reading mode locked to CLASSIC (Middle) for Daily 1 — no picker */}

            {/* Display name input */}
            <div className="mb-6">
                <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2 block">
                    Tvoje jméno v žebříčku
                </label>
                <div className="flex items-center gap-2">
                    <User size={14} weight="bold" className="text-white/40" />
                    <input
                        type="text"
                        value={displayName}
                        onChange={(e) => setDisplayName(e.target.value.slice(0, 32))}
                        placeholder="Anonym"
                        maxLength={32}
                        data-testid="inko-daily-name-input"
                        className="flex-1 bg-black/60 border border-white/15 focus:border-fa-cream outline-none font-mono text-sm text-fa-cream px-3 py-2 placeholder:text-white/25"
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8">
                <RuleCard icon={Clock} title={`${pacing.round_duration_s} s na hráče`} desc={`Nová indície každých ${pacing.clue_interval_s} s`} />
                <RuleCard icon={Star} title="1 kolo" desc="Max 100 bodů za dnešek" />
                <RuleCard icon={Trophy} title="Globální žebříček" desc="Skóre podle CET dne" />
            </div>

            <div className="grid grid-cols-1 gap-3">
                <ModeButton
                    label="Hrát · Middle · Medium"
                    testid="inko-daily-medium-btn"
                    color="fa-cyan"
                    desc="1 hráč · 30 s · fixní profil pro férový žebříček"
                    disabled={alreadyPlayed.medium !== null && alreadyPlayed.medium !== undefined}
                    playedScore={alreadyPlayed.medium}
                    onClick={() => onStart("medium")}
                />
            </div>

            <div className="mt-6 flex flex-wrap gap-3 items-center justify-between">
                <div className="font-mono text-[10px] uppercase tracking-wide text-white/30">
                    Éra dnešního hráče: <span className="text-fa-cream">{dailyPlayer.era}</span> · Skóre 100 → 10
                </div>
                <Link
                    to={`/inko/daily/leaderboard?date=${date}`}
                    data-testid="inko-daily-leaderboard-link"
                    className="font-mono text-[11px] uppercase tracking-widest px-3 py-1.5 border border-white/15 text-white/60 hover:border-fa-yellow hover:text-fa-yellow transition-colors flex items-center gap-2"
                >
                    <Trophy size={12} weight="bold" /> Žebříček
                </Link>
            </div>
        </div>
    );
}

function ModeButton({ label, testid, color, desc, disabled, playedScore, onClick }) {
    // Explicit static classes so Tailwind doesn't purge them.
    const colorMap = {
        "fa-mint": {
            border: "border-fa-mint/60",
            bg: "bg-fa-mint/10 hover:bg-fa-mint hover:text-black",
            text: "text-fa-mint",
        },
        "fa-cyan": {
            border: "border-fa-cyan/60",
            bg: "bg-fa-cyan/10 hover:bg-fa-cyan hover:text-black",
            text: "text-fa-cyan",
        },
        "fa-yellow": {
            border: "border-fa-yellow/60",
            bg: "bg-fa-yellow/10 hover:bg-fa-yellow hover:text-black",
            text: "text-fa-yellow",
        },
    };
    const c = colorMap[color] || colorMap["fa-mint"];
    if (disabled) {
        return (
            <div
                data-testid={testid + "-disabled"}
                className="border border-white/10 bg-black/30 p-6 opacity-70"
            >
                <div className="flex items-center justify-between mb-3">
                    <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
                        Mód · {label}
                    </span>
                    <Check size={16} weight="bold" className="text-white/40" />
                </div>
                <div className="font-mono text-2xl uppercase tracking-tight text-white/60">
                    Zahráno
                </div>
                <div className="mt-2 font-mono text-[11px] uppercase tracking-wide text-white/40">
                    Tvůj skóre: +{playedScore} b · Zítra znovu
                </div>
            </div>
        );
    }
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            className={`border ${c.border} ${c.bg} transition-colors p-6 text-left group`}
        >
            <div className="flex items-center justify-between mb-3">
                <span className={`font-mono text-[10px] uppercase tracking-[0.3em] ${c.text}`}>
                    Mód · {label}
                </span>
                <Play size={16} weight="fill" className={c.text} />
            </div>
            <div className="font-mono text-2xl uppercase tracking-tight text-fa-cream">
                {label}
            </div>
            <div className="mt-2 font-mono text-[11px] uppercase tracking-wide text-white/60">
                {desc}
            </div>
        </button>
    );
}

function RuleCard({ icon: Icon, title, desc }) {
    return (
        <div className="border border-white/10 bg-black/60 p-4">
            <Icon size={16} weight="bold" className="text-fa-cream mb-2" />
            <div className="font-mono text-xs uppercase tracking-wide text-fa-cream">{title}</div>
            <div className="mt-1 font-mono text-[10px] uppercase tracking-wide text-white/50">{desc}</div>
        </div>
    );
}

function HardInputWithAutocomplete({ guess, setGuess, onSubmit, disabled, allPlayers }) {
    const inputRef = useRef(null);
    const [highlighted, setHighlighted] = useState(-1);
    const [showList, setShowList] = useState(false);

    const suggestions = useMemo(() => {
        if (!guess.trim() || guess.trim().length < 2) return [];
        return autocompleteSearch(guess, allPlayers, 5);
    }, [guess, allPlayers]);

    useEffect(() => {
        if (!disabled && inputRef.current) inputRef.current.focus();
    }, [disabled]);

    // Reset highlight when suggestions change
    useEffect(() => {
        setHighlighted(-1);
    }, [suggestions.length]);

    const pickSuggestion = (val) => {
        setGuess(val);
        setShowList(false);
        inputRef.current?.focus();
    };

    const handleKeyDown = (e) => {
        if (!showList || suggestions.length === 0) return;
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlighted((h) => Math.min(h + 1, suggestions.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlighted((h) => Math.max(h - 1, -1));
        } else if (e.key === "Enter" && highlighted >= 0) {
            e.preventDefault();
            pickSuggestion(suggestions[highlighted]);
        } else if (e.key === "Escape") {
            setShowList(false);
        }
    };

    return (
        <div className="relative">
            <form
                onSubmit={(e) => { e.preventDefault(); onSubmit(guess); setShowList(false); }}
                className="flex items-center gap-2"
            >
                <input
                    ref={inputRef}
                    type="text"
                    value={guess}
                    onChange={(e) => { setGuess(e.target.value); setShowList(true); }}
                    onFocus={() => setShowList(true)}
                    onBlur={() => setTimeout(() => setShowList(false), 200)}
                    onKeyDown={handleKeyDown}
                    disabled={disabled}
                    placeholder="Zadej jméno hráče (KDB, Salah, Sigurdsson…)"
                    autoComplete="off"
                    data-testid="inko-daily-hard-input"
                    className="flex-1 bg-black/60 border border-white/15 focus:border-fa-cream outline-none font-mono text-sm text-fa-cream px-3 py-2 placeholder:text-white/25 disabled:opacity-40"
                />
                <button
                    type="submit"
                    disabled={disabled || !guess.trim()}
                    data-testid="inko-daily-hard-submit"
                    className="font-mono text-[11px] uppercase tracking-widest px-4 py-2 border border-fa-mint text-fa-mint hover:bg-fa-mint hover:text-black disabled:opacity-30 transition-colors"
                >
                    Uhodnout
                </button>
            </form>

            {showList && suggestions.length > 0 && !disabled && (
                <div
                    className="absolute z-20 left-0 right-0 mt-1 border border-white/15 bg-black/95 backdrop-blur-sm shadow-lg"
                    data-testid="inko-daily-autocomplete"
                >
                    {suggestions.map((name, i) => (
                        <button
                            key={name}
                            type="button"
                            onMouseDown={(e) => { e.preventDefault(); pickSuggestion(name); }}
                            onMouseEnter={() => setHighlighted(i)}
                            data-testid={`inko-daily-autocomplete-${normalize(name)}`}
                            className={`w-full text-left px-3 py-2 font-mono text-sm border-b border-white/5 last:border-b-0 transition-colors ${
                                highlighted === i ? "bg-fa-cream/10 text-fa-cream" : "text-white/70 hover:text-fa-cream"
                            }`}
                        >
                            {name}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

function ReadingModePicker({ readingMode, setReadingMode }) {
    const descriptions = {
        slow: "10 s / indícii · 60 s / hráče · víc času na čtení",
        classic: "5 s / indícii · 30 s / hráče · plné věty",
        keywords: "5 s / indícii · 30 s / hráče · jen holá klíčová slova",
        porno: "5 s / indícii · 30 s / hráče · reálné GW momenty, body, góly",
    };
    return (
        <div className="mb-6">
            <div className="flex items-center gap-2 mb-2">
                <Gauge size={12} weight="bold" className="text-white/40" />
                <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
                    Rytmus čtení
                </label>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="inko-reading-mode-picker">
                {READING_MODES.map((m) => {
                    const active = readingMode === m;
                    return (
                        <button
                            key={m}
                            type="button"
                            onClick={() => setReadingMode(m)}
                            data-testid={`inko-reading-mode-${m}`}
                            className={`border p-3 text-left transition-colors ${
                                active
                                    ? "border-fa-cream bg-fa-cream/10 text-fa-cream"
                                    : "border-white/15 text-white/50 hover:border-white/40 hover:text-fa-cream"
                            }`}
                        >
                            <div className="font-mono text-sm uppercase tracking-wide">
                                {READING_MODE_LABELS[m]}
                            </div>
                            <div className="mt-1 font-mono text-[10px] uppercase tracking-wide text-white/40">
                                {descriptions[m]}
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

// ---- Round (single-player variant of INKO) -----------------------------

function Round({ player, allPlayers, mode, readingMode, onFinish }) {
    const pacing = pacingConfig(readingMode);
    const ROUND_DURATION = pacing.round_duration_s;
    const CLUE_INTERVAL = pacing.clue_interval_s;
    const doKeywords = pacing.keywords;
    const doPorno = pacing.porno;

    const [elapsed, setElapsed] = useState(0);
    const [guess, setGuess] = useState("");
    const [submitted, setSubmitted] = useState(null);
    const [reportedClues, setReportedClues] = useState(new Set());
    const [hintPurchased, setHintPurchased] = useState(false);
    const hintCost = paidSurnameHintCost(mode);
    const hintValue = useMemo(() => paidSurnameHint(player), [player]);
    const intervalRef = useRef(null);
    const finishTimeoutRef = useRef(null);
    const pendingFinishRef = useRef(null);
    const startTimeRef = useRef(Date.now());

    const options = useMemo(
        () => (mode === "hard" ? [] : buildOptions(player, allPlayers, mode, managersData.managers || [])),
        [player, allPlayers, mode]
    );

    const sortedClues = useMemo(
        () => (readingMode === "porno"
            ? getPornoClues(player)
            : [...player.clues].sort((a, b) => a.order - b.order)),
        [player, readingMode]
    );

    // Per-player shuffled micro-clues (seeded → deterministic per player, fair
    // for Daily leaderboard while still varying which field lands on #1).
    const shuffledMicros = useMemo(
        () => shuffleMicroClues(player.micro_clues, player.id),
        [player.id, player.micro_clues]  // eslint-disable-line react-hooks/exhaustive-deps
    );

    const cluesRevealed = Math.min(
        Math.floor(elapsed / CLUE_INTERVAL) + 1,
        sortedClues.length
    );
    const currentScore = SCORE_TABLE[Math.min(cluesRevealed - 1, SCORE_TABLE.length - 1)];
    const timeLeft = Math.max(0, ROUND_DURATION - elapsed);

    useEffect(() => {
        if (submitted) return;
        intervalRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
        return () => clearInterval(intervalRef.current);
    }, [submitted]);

    useEffect(() => {
        if (submitted) return;
        if (elapsed >= ROUND_DURATION) {
            handleEnd({ correct: false, score: 0, reason: "timeout" });
        }
    }, [elapsed, submitted, ROUND_DURATION]);

    useEffect(() => {
        return () => {
            clearTimeout(finishTimeoutRef.current);
            clearInterval(intervalRef.current);
        };
    }, []);

    const applyHintPenalty = (baseScore) => (hintPurchased ? baseScore - hintCost : baseScore);

    const handleEnd = ({ correct, score, reason }) => {
        if (submitted) return;
        clearInterval(intervalRef.current);
        const bonuses = computeRoundBonuses({ player, correct, cluesRevealed });
        const bonusPoints = bonuses.reduce((s, b) => s + b.points, 0);
        const finalScore = applyHintPenalty(score + bonusPoints);
        const timeTaken = (Date.now() - startTimeRef.current) / 1000;
        setSubmitted({ correct, score: finalScore, reason, bonuses });
        const payload = {
            correct,
            score: finalScore,
            cluesRevealed,
            timeTaken,
            hintUsed: hintPurchased,
            bonuses,
        };
        pendingFinishRef.current = payload;
        finishTimeoutRef.current = setTimeout(() => {
            if (pendingFinishRef.current) {
                pendingFinishRef.current = null;
                onFinish(payload);
            }
        }, FEEDBACK_MS);
    };

    const continueNow = () => {
        if (!pendingFinishRef.current) return;
        clearTimeout(finishTimeoutRef.current);
        const payload = pendingFinishRef.current;
        pendingFinishRef.current = null;
        onFinish(payload);
    };

    const handleSubmitGuess = (text) => {
        if (submitted) return;
        const g = (text || "").trim();
        if (!g) return;
        // Hard mode uses fuzzy matching (typo-tolerant); Easy is exact (options are pre-matched).
        const correct = mode === "hard" ? isCorrectFuzzy(g, player.aliases) : isCorrect(g, player.aliases);
        handleEnd({ correct, score: correct ? currentScore : 0, reason: correct ? "correct" : "wrong" });
    };

    const handleReportClue = async (order) => {
        if (reportedClues.has(order)) return;
        setReportedClues((prev) => new Set([...prev, order]));
        try {
            const clientId = ensureClientId();
            await axios.post(`${API}/inko/report-clue`, {
                player_id: player.id,
                clue_order: order,
                client_id: clientId,
                reason: null,
            });
        } catch {
            // silent
        }
    };

    const handleSkip = () => handleEnd({ correct: false, score: 0, reason: "skip" });

    const progressPct = (elapsed / ROUND_DURATION) * 100;

    return (
        <div className="border border-white/10 bg-black/40" data-testid="inko-daily-round">
            <div className="border-b border-white/10 p-4 flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-4">
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">Mód</div>
                        <div className="font-mono text-2xl text-fa-cream uppercase">{mode}</div>
                    </div>
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">Za trefu</div>
                        <div
                            className="font-mono text-2xl text-fa-yellow"
                            data-testid="inko-daily-current-score"
                        >
                            +{currentScore}
                        </div>
                    </div>
                </div>
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">Čas</div>
                    <div
                        className="font-mono text-2xl text-fa-cream tabular-nums"
                        data-testid="inko-daily-timer"
                    >
                        {timeLeft}s
                    </div>
                </div>
            </div>

            <div className="h-1 bg-white/5">
                <div className="h-full bg-fa-mint transition-all" style={{ width: `${progressPct}%` }} />
            </div>

            <div className="p-3 sm:p-6 space-y-3">
                {sortedClues.map((clue, i) => {
                    const revealed = i < cluesRevealed;
                    const isReported = reportedClues.has(clue.order);
                    const micro = doKeywords
                        ? (shuffledMicros?.[clue.order - 1] || microClueFor(player, clue.order))
                        : null;
                    const keywords = doKeywords && !micro ? extractKeywords(clue.text) : null;
                    return (
                        <div
                            key={clue.order}
                            data-testid={`inko-daily-clue-${clue.order}`}
                            className={`border p-3 transition-all ${
                                revealed ? "border-white/20 bg-black/50" : "border-dashed border-white/5 bg-black/20"
                            }`}
                        >
                            <div className="flex items-start gap-3">
                                <div
                                    className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 border shrink-0 ${
                                        revealed ? "border-fa-mint/40 text-fa-mint" : "border-white/10 text-white/20"
                                    }`}
                                >
                                    #{clue.order}
                                </div>
                                <div
                                    className={`font-mono text-sm leading-relaxed flex-1 ${
                                        revealed ? "text-fa-cream" : "text-white/10 blur-sm select-none"
                                    }`}
                                >
                                    {revealed ? (
                                        doKeywords ? (
                                            micro ? (
                                                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-fa-yellow/15 border border-fa-yellow/40">
                                                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-fa-yellow/60">
                                                        {MICRO_CLUE_LABELS[micro.field] || micro.field}
                                                    </span>
                                                    <span className="font-mono text-base uppercase tracking-wide text-fa-yellow">
                                                        {SURNAME_LEAK_FIELDS.has(micro.field)
                                                            ? surnameHintFor(player, micro.value)
                                                            : micro.value}
                                                    </span>
                                                </div>
                                            ) : (
                                                <div className="flex flex-wrap gap-1.5">
                                                    {keywords.map((k, ki) => (
                                                        <span
                                                            key={ki}
                                                            className="inline-block px-2 py-1 bg-fa-yellow/15 border border-fa-yellow/40 text-fa-yellow font-mono text-sm"
                                                        >
                                                            {k}
                                                        </span>
                                                    ))}
                                                </div>
                                            )
                                        ) : (
                                            clue.text
                                        )
                                    ) : (
                                        "•••••••••••••••••••••••••••••"
                                    )}
                                </div>
                                {revealed && (
                                    <button
                                        type="button"
                                        title={isReported ? "Nahlášeno" : "Nahlásit chybu v indícii"}
                                        onClick={() => handleReportClue(clue.order)}
                                        disabled={isReported}
                                        data-testid={`inko-daily-report-clue-${clue.order}`}
                                        className={`shrink-0 p-1 transition-colors ${
                                            isReported
                                                ? "text-fa-yellow/70 cursor-default"
                                                : "text-white/20 hover:text-fa-yellow"
                                        }`}
                                    >
                                        <Flag size={12} weight={isReported ? "fill" : "regular"} />
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="border-t border-white/10 p-3 sm:p-6">
                {mode !== "hard" ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="inko-daily-options">
                        {options.map((opt) => {
                            const isTheAnswer = submitted && isCorrect(opt, player.aliases);
                            let cls = "border-white/15 hover:border-fa-cream hover:bg-fa-cream/5";
                            if (submitted) {
                                cls = isTheAnswer
                                    ? "border-fa-mint bg-fa-mint/20 text-fa-mint"
                                    : "border-white/5 text-white/30";
                            }
                            return (
                                <button
                                    key={opt}
                                    type="button"
                                    disabled={!!submitted}
                                    onClick={() => handleSubmitGuess(opt)}
                                    data-testid={`inko-daily-option-${normalize(opt)}`}
                                    className={`border p-3 text-left font-mono text-xs sm:text-sm uppercase tracking-wide transition-colors break-words ${cls}`}
                                >
                                    {opt}
                                </button>
                            );
                        })}
                    </div>
                ) : (
                    <HardInputWithAutocomplete
                        guess={guess}
                        setGuess={setGuess}
                        onSubmit={handleSubmitGuess}
                        disabled={!!submitted}
                        allPlayers={allPlayers}
                    />
                )}

                <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div className="order-2 sm:order-1 w-full sm:w-auto">
                        {hintPurchased ? (
                            <div
                                data-testid="inko-daily-hint-purchased"
                                className="font-mono text-[11px] uppercase tracking-widest px-3 py-2 border border-fa-cream/40 bg-fa-cream/10 text-fa-cream flex flex-wrap items-center gap-2"
                            >
                                <Eye size={12} weight="bold" />
                                Příjmení:
                                <span className="text-fa-yellow" data-testid="inko-daily-hint-value">
                                    {hintValue}
                                </span>
                                <span className="text-white/40">−{hintCost} b</span>
                            </div>
                        ) : (
                            <button
                                type="button"
                                onClick={() => setHintPurchased(true)}
                                disabled={!!submitted || cluesRevealed < PAID_HINT_MIN_CLUE}
                                data-testid="inko-daily-buy-hint-btn"
                                title={cluesRevealed < PAID_HINT_MIN_CLUE
                                    ? `Dostupné až od indície #${PAID_HINT_MIN_CLUE}`
                                    : `Odhalí zamaskované příjmení za −${hintCost} b`}
                                className="w-full sm:w-auto justify-center sm:justify-start font-mono text-[11px] uppercase tracking-widest px-3 py-2 border border-fa-cream/30 text-fa-cream/70 hover:border-fa-cream hover:bg-fa-cream/10 hover:text-fa-cream disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                            >
                                <Eye size={12} weight="bold" />
                                {cluesRevealed < PAID_HINT_MIN_CLUE
                                    ? `Nápověda · od #${PAID_HINT_MIN_CLUE}`
                                    : `Nápověda příjmení (−${hintCost} b)`}
                            </button>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={handleSkip}
                        disabled={!!submitted}
                        data-testid="inko-daily-skip-btn"
                        className="order-1 sm:order-2 w-full sm:w-auto justify-center sm:justify-start font-mono text-[11px] uppercase tracking-widest px-3 py-2 border border-white/15 text-white/60 hover:border-fa-yellow hover:text-fa-yellow disabled:opacity-30 transition-colors flex items-center gap-2"
                    >
                        <SkipForward size={12} weight="bold" /> Skip (0 b)
                    </button>
                </div>
            </div>

            {submitted && (
                <div className="border-t border-white/10 p-3 sm:p-6 bg-black/70" data-testid="inko-daily-feedback">
                    {submitted.correct ? (
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 border border-fa-mint bg-fa-mint/20 flex items-center justify-center">
                                <Check size={18} weight="bold" className="text-fa-mint" />
                            </div>
                            <div>
                                <div className="font-mono text-xs uppercase tracking-widest text-fa-mint">
                                    Trefa · +{submitted.score} bodů
                                </div>
                                <div className="font-mono text-xl text-fa-cream mt-1">{player.answer}</div>
                            </div>
                        </div>
                    ) : (
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 border border-red-400 bg-red-400/10 flex items-center justify-center">
                                <X size={18} weight="bold" className="text-red-400" />
                            </div>
                            <div>
                                <div className="font-mono text-xs uppercase tracking-widest text-red-300">
                                    {submitted.reason === "timeout"
                                        ? "Čas vypršel"
                                        : submitted.reason === "skip"
                                        ? "Skip · 0 bodů"
                                        : "Špatně · 0 bodů"}
                                </div>
                                <div className="font-mono text-xl text-fa-cream mt-1">
                                    Správně: {player.answer}
                                </div>
                            </div>
                        </div>
                    )}

                    {submitted.correct && (submitted.bonuses || []).length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2" data-testid="inko-daily-bonus-chips">
                            {submitted.bonuses.map((b) => (
                                <div
                                    key={b.id}
                                    data-testid={`inko-daily-bonus-${b.id}`}
                                    className="inline-flex items-center gap-2 px-3 py-1.5 border border-fa-yellow bg-fa-yellow/15 font-mono text-[11px] uppercase tracking-widest text-fa-yellow"
                                >
                                    <Star size={11} weight="fill" />
                                    {b.label}
                                    <span className="text-fa-cream">+{b.points} b</span>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Cross-fantasy lore box */}
                    {(() => {
                        const lore = getCrossFantasyLore(player);
                        if (!lore || (!lore.summary && !lore.hook)) return null;
                        return (
                            <div
                                className="mt-4 border border-fa-cyan/40 bg-fa-cyan/5 p-3"
                                data-testid="inko-daily-cross-fantasy-lore"
                            >
                                <div className="mb-2 flex items-center gap-2 flex-wrap">
                                    <div className="inline-flex items-center gap-2 px-2 py-1 border border-fa-cyan bg-fa-cyan/15 font-mono text-[10px] uppercase tracking-widest text-fa-cyan">
                                        <Globe size={11} weight="bold" /> European Fantasy
                                    </div>
                                    {lore.competitions.slice(0, 3).map((c) => (
                                        <span
                                            key={c}
                                            className="font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 border border-white/15 text-white/50"
                                        >
                                            {c}
                                        </span>
                                    ))}
                                </div>
                                {lore.summary && (
                                    <div className="font-mono text-xs text-fa-cream leading-snug">
                                        {lore.summary}
                                    </div>
                                )}
                                {lore.hook && lore.hook !== lore.summary && (
                                    <div className="mt-1 font-mono text-[11px] text-fa-cream/70 leading-snug">
                                        {lore.hook}
                                    </div>
                                )}
                            </div>
                        );
                    })()}

                    {/* Rare manager badge */}
                    {submitted.correct && isManager(player) && (
                        <div className="mt-4 border border-fa-yellow/40 bg-fa-yellow/5 p-3" data-testid="inko-daily-manager-lore">
                            <div className="mb-1 inline-flex items-center gap-2 px-2 py-1 border border-fa-yellow bg-fa-yellow/15 font-mono text-[10px] uppercase tracking-widest text-fa-yellow">
                                <Star size={11} weight="fill" /> Rare Manager · 2× body
                            </div>
                            {player.story && (
                                <div className="mt-1 font-mono text-xs text-fa-cream leading-snug">
                                    {player.story}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Generic "report bug on this player" button */}
                    <div className="mt-4 flex justify-end gap-2">
                        <button
                            type="button"
                            onClick={() => handleReportClue(0)}
                            disabled={reportedClues.has(0)}
                            data-testid="inko-daily-report-player-btn"
                            className={`font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border transition-colors flex items-center gap-2 ${
                                reportedClues.has(0)
                                    ? "border-fa-yellow/40 text-fa-yellow/60 cursor-default"
                                    : "border-white/15 text-white/50 hover:border-fa-yellow hover:text-fa-yellow"
                            }`}
                        >
                            <Flag size={11} weight={reportedClues.has(0) ? "fill" : "regular"} />
                            {reportedClues.has(0) ? "Nahlášeno · díky" : "Nahlásit chybu"}
                        </button>
                        <button
                            type="button"
                            onClick={continueNow}
                            data-testid="inko-daily-continue-btn"
                            className="font-mono text-[10px] uppercase tracking-widest px-4 py-1.5 border border-fa-mint bg-fa-mint/15 text-fa-mint hover:bg-fa-mint hover:text-black transition-colors font-bold"
                        >
                            Pokračovat →
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

// ---- Result screen -----------------------------------------------------

function ResultScreen({ date, dailyPlayer, result, streak, displayName, onBack }) {
    const [copied, setCopied] = useState(false);
    const [leaderboard, setLeaderboard] = useState(null);
    const [loadingLb, setLoadingLb] = useState(true);
    const [lbMode, setLbMode] = useState(result.mode);
    const [lbReadingMode, setLbReadingMode] = useState(result.reading_mode || "classic");

    useEffect(() => {
        setLoadingLb(true);
        (async () => {
            try {
                const params = new URLSearchParams({
                    date,
                    limit: "10",
                    mode: lbMode,
                    reading_mode: lbReadingMode,
                });
                const r = await axios.get(`${API}/inko/daily/leaderboard?${params}`);
                setLeaderboard(r.data);
            } catch {
                setLeaderboard({ rows: [], total_players: 0 });
            } finally {
                setLoadingLb(false);
            }
        })();
    }, [date, lbMode, lbReadingMode, result.score]);

    const shareText = useMemo(() => {
        const bar = Array.from({ length: 6 }, (_, i) =>
            result.correct && i < result.cluesRevealed
                ? (i === result.cluesRevealed - 1 ? "\ud83d\udfe2" : "\u2b1c")
                : i < result.cluesRevealed
                ? "\ud83d\udd34"
                : "\u2b1b"
        ).join("");
        return `INKO Daily ${date}
${result.correct ? "\u2705" : "\u274c"} ${result.score}/100 (${result.cluesRevealed}/6 indícií · ${result.mode})
${bar}
Streak: ${streak} ${streakLabel(streak)}
${window.location.origin}/inko/daily`;
    }, [date, result, streak]);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(shareText);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // Fallback: show textarea
        }
    };

    return (
        <div className="space-y-6">
            <div className="border border-white/10 bg-black/40 p-4 sm:p-8" data-testid="inko-daily-result">
                <div className="mb-6 flex items-center gap-3">
                    <Trophy size={20} weight="bold" className="text-fa-yellow" />
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-yellow">
                        Denní výzva · {result.mode}
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
                    <div className={`border p-4 ${result.correct ? "border-fa-mint/50 bg-fa-mint/5" : "border-red-400/40 bg-red-400/5"}`}>
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">Skóre</div>
                        <div
                            className={`mt-1 font-mono text-4xl ${result.correct ? "text-fa-mint" : "text-red-300"}`}
                            data-testid="inko-daily-final-score"
                        >
                            {result.score}
                            <span className="text-lg text-white/40">/100</span>
                        </div>
                    </div>
                    <div className="border border-white/10 bg-black/60 p-4">
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">Indícií</div>
                        <div className="mt-1 font-mono text-4xl text-fa-cream">
                            {result.cluesRevealed}
                            <span className="text-lg text-white/40">/6</span>
                        </div>
                    </div>
                    <div className="border border-fa-yellow/40 bg-fa-yellow/5 p-4">
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">Streak</div>
                        <div
                            className="mt-1 font-mono text-4xl text-fa-yellow flex items-center gap-2"
                            data-testid="inko-daily-result-streak"
                        >
                            {streak}
                            <Fire size={22} weight="fill" className="text-fa-yellow/70" />
                        </div>
                    </div>
                </div>

                <div className="border border-white/10 bg-black/60 p-4 mb-4">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 mb-1">
                        Hráč dne
                    </div>
                    <div className="font-mono text-xl text-fa-cream">{dailyPlayer.answer}</div>
                    <div className="font-mono text-[10px] uppercase tracking-wide text-white/40 mt-1">
                        {dailyPlayer.era} · {dailyPlayer.position_hint}
                    </div>
                </div>

                {/* Share */}
                <div className="flex flex-wrap items-center gap-3">
                    <button
                        type="button"
                        onClick={handleCopy}
                        data-testid="inko-daily-share-btn"
                        className="font-mono text-[11px] uppercase tracking-widest px-4 py-2 border border-fa-mint text-fa-mint hover:bg-fa-mint hover:text-black transition-colors flex items-center gap-2"
                    >
                        <Copy size={12} weight="bold" /> {copied ? "Zkopírováno!" : "Sdílet výsledek"}
                    </button>
                    <button
                        type="button"
                        onClick={onBack}
                        data-testid="inko-daily-back-btn"
                        className="font-mono text-[11px] uppercase tracking-widest px-4 py-2 border border-white/15 text-white/60 hover:border-fa-cream hover:text-fa-cream transition-colors flex items-center gap-2"
                    >
                        <ArrowClockwise size={12} weight="bold" /> Změnit mód
                    </button>
                    <Link
                        to="/inko"
                        data-testid="inko-daily-goto-classic"
                        className="font-mono text-[11px] uppercase tracking-widest px-4 py-2 border border-white/15 text-white/60 hover:border-fa-cream hover:text-fa-cream transition-colors flex items-center gap-2"
                    >
                        Klasická hra
                    </Link>
                </div>

                {/* Preview share */}
                <pre className="mt-4 font-mono text-[11px] leading-relaxed text-white/40 whitespace-pre-wrap border border-dashed border-white/10 p-3 bg-black/30">
                    {shareText}
                </pre>
            </div>

            {/* Leaderboard */}
            <div className="border border-white/10 bg-black/40 p-4 sm:p-6" data-testid="inko-daily-leaderboard">
                <div className="mb-4 flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-2">
                        <Trophy size={16} weight="bold" className="text-fa-yellow" />
                        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-yellow">
                            Žebříček · {date}
                        </span>
                    </div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                        {leaderboard?.total_players ?? 0} hráčů v této kombinaci
                    </div>
                </div>

                {/* Leaderboard locked to the tier played (Middle+Medium = Padawan). */}
                {(() => {
                    const playedTier = getTier(lbMode, lbReadingMode);
                    return (
                        <div
                            className="mb-4 inline-flex items-center gap-2 border border-white/15 bg-black/40 px-3 py-1.5"
                            data-testid="inko-daily-lb-tier-badge"
                        >
                            <Trophy size={11} weight="fill" className="text-fa-yellow" />
                            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-yellow">
                                Žebříček · {playedTier ? playedTier.label : `${lbMode} · ${lbReadingMode}`}
                            </span>
                        </div>
                    );
                })()}
                {loadingLb ? (
                    <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">Načítám…</div>
                ) : (leaderboard?.rows?.length ?? 0) === 0 ? (
                    <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                        Zatím žádné výsledky — buď první.
                    </div>
                ) : (
                    <div className="space-y-1">
                        {leaderboard.rows.map((r, i) => {
                            const myClientId = localStorage.getItem(LS.clientId);
                            const isMe = r.client_id && myClientId && r.client_id === myClientId;
                            return (
                                <div
                                    key={i}
                                    className={`flex items-center justify-between border px-3 py-2 ${
                                        isMe ? "border-fa-mint/40 bg-fa-mint/5" : "border-white/5 bg-black/40"
                                    }`}
                                >
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div className="font-mono text-[10px] text-white/30 w-6">
                                            {String(i + 1).padStart(2, "0")}
                                        </div>
                                        <div className={`font-mono text-sm truncate ${isMe ? "text-fa-mint" : "text-fa-cream"}`}>
                                            {r.display_name}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-4 shrink-0">
                                        <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                                            {r.clues_used} indícií
                                        </div>
                                        <div className={`font-mono text-sm ${r.correct ? "text-fa-mint" : "text-white/30"}`}>
                                            +{r.score}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}
